<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="handleNotificationClose"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-account-plus</v-icon>
        {{ isEditMode ? 'Edit Client' : 'Add New Client' }}
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <!-- Client Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Client Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newClient.cifNumber"
                label="CIF Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-identifier"
                readonly
              />
            </v-col>

          </v-row>

          <v-row>
            <!-- Personal Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Personal Information</h3>
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="newClient.firstName"
                label="First Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'First name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="newClient.secondName"
                label="Second Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-outline"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="newClient.surname"
                label="Surname"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-outline"
                :rules="[v => !!v || 'Surname is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newClient.idNumber"
                label="ID Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-card-account-details"
                :rules="idValidationRules"
                @input="formatIdInput"
                placeholder="901101 0000 00 0"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newClient.sarsNumber"
                label="SARS Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-receipt"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Contact Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Contact Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newClient.phone"
                label="Phone Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                :rules="phoneValidationRules"
                @input="formatPhoneInput"
                placeholder="074 551 1155"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newClient.email"
                label="Email Address"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                :rules="[
                  v => !v || /.+@.+\..+/.test(v) || 'Must be a valid email',
                  v => true
                ]"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Address Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Address Information</h3>
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                v-model="newClient.address"
                label="Street Address"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-home"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newClient.city"
                label="City"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-city"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newClient.postalCode"
                label="Postal Code"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-mailbox"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Membership Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Membership Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-select
                v-model="newClient.membership"
                :items="membershipLevels"
                label="Membership Level"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-star"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newClient.membershipNumber"
                label="Membership Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-card-membership"
                :disabled="newClient.membership === 'None'"
              />
            </v-col>
            

            

          </v-row>

          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                color="#002060"
                dark
                rounded
                size="large"
                :disabled="!valid || loading"
                :loading="loading"
                @click="saveClient"
                class="mr-4"
              >
                <v-icon left>{{ isEditMode ? 'mdi-content-save' : 'mdi-account-plus' }}</v-icon>
                {{ loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Client' : 'Add Client') }}
              </v-btn>
              
              <v-btn
                color="grey"
                dark
                rounded
                size="large"
                @click="$router.back()"
              >
                <v-icon left>mdi-close</v-icon>
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import { CIFGenerator } from '../utils/cifGenerator';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneInput, formatPhoneNumber, getPhoneValidationRules } from '../utils/phoneFormatter';
import { formatIdInput, getIdValidationRules } from '../utils/idNumberFormatter';

export default {
  name: 'AddClient',
  components: {
    NotificationDialog
  },
  computed: {
    ...mapGetters(['user'])
  },
  data() {
    return {
      valid: false,
      loading: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',

      isEditMode: false,
      clientId: null,
      newClient: {
        cifNumber: '',
        firstName: '',
        secondName: '',
        surname: '',
        idNumber: '',
        sarsNumber: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        postalCode: '',
        membership: '',
        membershipNumber: ''
      },
      membershipLevels: ['None', 'Domestic', 'Individual', 'Individual & Domestic']
    };
  },
  computed: {
    phoneValidationRules() {
      return [
        v => !v || v.replace(/\D/g, '').length === 10 || 'Phone number must be 10 digits (e.g., 074 551 1155)'
      ];
    },
    idValidationRules() {
      return getIdValidationRules();
    }
  },
  methods: {


    formatIdInput() {
      this.newClient.idNumber = formatIdInput(this.newClient.idNumber);
    },

    formatPhoneInput() {
      this.newClient.phone = formatPhoneInput(this.newClient.phone);
    },

    async loadClientData() {
      if (!this.clientId) {
        console.error('loadClientData: clientId is missing');
        return;
      }
      
      console.log('loadClientData called with clientId:', this.clientId);
      
      try {
        this.loading = true;
        const clientDocRef = doc(db, 'clients', this.clientId);
        console.log('Fetching document from:', clientDocRef.path);
        
        const clientDoc = await getDoc(clientDocRef);
        
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          console.log('Client data loaded:', clientData);
          console.log('Client document ID:', clientDoc.id);
          
          this.newClient = {
            cifNumber: clientData.cifNumber || '',
            firstName: clientData.firstName || '',
            secondName: clientData.secondName || '',
            surname: clientData.surname || '',
            idNumber: clientData.idNumber || '',
            sarsNumber: clientData.sarsNumber || '',
            phone: formatPhoneNumber(clientData.phone || ''),
            email: clientData.email || '',
            address: clientData.address || '',
            city: clientData.city || '',
            postalCode: clientData.postalCode || '',
            membership: clientData.membership || '',
            membershipNumber: clientData.membershipNumber || ''
          };
          console.log('Form data set:', this.newClient);
        } else {
          console.error('Client document not found for ID:', this.clientId);
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Client not found. Please check the client ID.';
          this.showNotification = true;
          // Navigate back if client not found
          setTimeout(() => {
            this.$router.back();
          }, 2000);
        }
      } catch (error) {
        console.error('Error loading client data:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          clientId: this.clientId
        });
        
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to load client data. Please try again.';
        this.showNotification = true;
        // Navigate back on error
        setTimeout(() => {
          this.$router.back();
        }, 2000);
      } finally {
        this.loading = false;
      }
    },

    async saveClient() {
      console.log('saveClient called - isEditMode:', this.isEditMode, 'clientId:', this.clientId);
      console.log('Form data:', this.newClient);
      
      if (!this.$refs.form.validate()) {
        console.log('Form validation failed');
        return;
      }
      
      console.log('Form validation passed');
      
      // Additional validation for edit mode
      if (this.isEditMode && !this.clientId) {
        console.error('Client ID is missing for edit mode');
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Client ID is missing. Cannot update client.';
        this.showNotification = true;
        return;
      }
      

      
      this.loading = true;
      this.showNotification = false;
      
      try {
        if (this.isEditMode) {
          console.log('Calling updateClient...');
          await this.updateClient();
        } else {
          console.log('Calling addClient...');
          await this.addClient();
        }
      } catch (error) {
        console.error('Error saving client:', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to save client. Please try again.';
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },

    async addClient() {
      try {
        // Generate CIF number automatically if not provided
        if (!this.newClient.cifNumber) {
          try {
            const cifNumber = await CIFGenerator.generateCIF('individual');
            this.newClient.cifNumber = cifNumber;
          } catch (error) {
            console.error('Error generating CIF, using fallback:', error);
            const cifNumber = CIFGenerator.generateTestCIF('individual');
            this.newClient.cifNumber = cifNumber;
          }
        }
        
        // Create client data object
        const clientData = {
          cifNumber: this.newClient.cifNumber,
          firstName: this.newClient.firstName,
          secondName: this.newClient.secondName || '',
          surname: this.newClient.surname,
          idNumber: this.newClient.idNumber.replace(/\s/g, ''),
          sarsNumber: this.newClient.sarsNumber || '',
          phone: this.newClient.phone,
          email: this.newClient.email,
          address: this.newClient.address,
          city: this.newClient.city,
          postalCode: this.newClient.postalCode,
          membership: this.newClient.membership,
          membershipNumber: this.newClient.membership === 'None' ? '' : this.newClient.membershipNumber,
          membershipStatus: this.newClient.membership === 'None' ? 'None' : (this.newClient.membershipStatus || 'Active'),
          clientType: 'individual',
          status: 'active',
          dateAdded: new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }),
          createdAt: new Date().toISOString()
        };
        
        // Add to Firestore
        const docRef = await addDoc(collection(db, 'clients'), clientData);
        console.log('Client added with ID:', docRef.id);
        
        // Log client creation activity
        const createdBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
        await ActivityLogger.clientCreated(createdBy, this.user?.uid, `${clientData.firstName} ${clientData.surname}`);
        
        // Show success notification
        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'Client added successfully!';
        this.showNotification = true;
        
      } catch (error) {
        console.error('Error adding client:', error);
        
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to add client. Please try again.';
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },

    async updateClient() {
      console.log('updateClient called with clientId:', this.clientId);
      
      // Validate client ID
      if (!this.clientId) {
        console.error('Client ID is missing');
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Client ID is missing. Cannot update client.';
        this.showNotification = true;
        return;
      }
      
      try {
        // First, check if the document exists
        const clientDocRef = doc(db, 'clients', this.clientId);
        const clientDoc = await getDoc(clientDocRef);
        
        if (!clientDoc.exists()) {
          console.error('Client document does not exist:', this.clientId);
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Client not found. The client may have been deleted.';
          this.showNotification = true;
          return;
        }
        
        console.log('Client document exists, proceeding with update');
        
        // Create client data object for update
        const clientData = {
          cifNumber: this.newClient.cifNumber, // Preserve CIF number
          firstName: this.newClient.firstName,
          secondName: this.newClient.secondName || '',
          surname: this.newClient.surname,
          idNumber: this.newClient.idNumber.replace(/\s/g, ''),
          sarsNumber: this.newClient.sarsNumber || '',
          phone: this.newClient.phone,
          email: this.newClient.email,
          address: this.newClient.address,
          city: this.newClient.city,
          postalCode: this.newClient.postalCode,
          membership: this.newClient.membership,
          membershipNumber: this.newClient.membership === 'None' ? '' : this.newClient.membershipNumber,
          membershipStatus: this.newClient.membership === 'None' ? 'None' : 'Active',
          clientType: 'individual', // Preserve client type
          status: 'active', // Preserve status
          updatedAt: new Date().toISOString()
        };
        
        console.log('Client data to update:', clientData);
        console.log('Checking for undefined/null values...');
        Object.keys(clientData).forEach(key => {
          if (clientData[key] === undefined || clientData[key] === null) {
            console.warn(`Field ${key} is ${clientData[key]}`);
          }
        });
        
        // Update in Firestore
        console.log('Updating document with ID:', this.clientId, 'data:', clientData);
        console.log('Document reference path:', clientDocRef.path);
        
        try {
          await updateDoc(clientDocRef, clientData);
          console.log('Client updated successfully with ID:', this.clientId);
        } catch (updateError) {
          console.error('Firestore update error:', updateError);
          console.error('Error code:', updateError.code);
          console.error('Error message:', updateError.message);
          throw updateError;
        }
        
        // Log client update activity (temporarily disabled for testing)
        try {
          const updatedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
          console.log('Logging activity for user:', updatedBy, 'uid:', this.user?.uid);
          await ActivityLogger.clientUpdated(updatedBy, this.user?.uid, `${clientData.firstName} ${clientData.surname}`);
          console.log('Activity logged successfully');
        } catch (activityError) {
          console.error('Error logging activity (non-critical):', activityError);
          // Don't fail the update if activity logging fails
        }
        
        // Show success notification
        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'Client updated successfully!';
        this.showNotification = true;
        
        // Navigate back after successful update
        setTimeout(() => {
          this.$router.back();
        }, 2000);
        
      } catch (error) {
        console.error('Error updating client:', error);
        
        // Provide more specific error messages
        let errorMessage = 'Failed to update client. Please try again.';
        
        if (error.code === 'permission-denied') {
          errorMessage = 'Permission denied. You may not have the right to update this client.';
        } else if (error.code === 'not-found') {
          errorMessage = 'Client not found. The client may have been deleted.';
        } else if (error.code === 'unavailable') {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        }
        
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = errorMessage;
        this.showNotification = true;
      }
    },

    handleNotificationClose() {
      this.showNotification = false;
      if (this.notificationType === 'success') {
        if (this.isEditMode) {
          // In edit mode, just go back to the previous page
          this.$router.back();
        } else {
          // In add mode, reset form and go back
          this.resetForm();
          this.$router.back();
        }
      }
    },
    
    resetForm() {
      this.newClient = {
        cifNumber: '',
        firstName: '',
        secondName: '',
        surname: '',
        idNumber: '',
        sarsNumber: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        postalCode: '',
        membership: '',
        membershipNumber: ''
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    
    resetValidation() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    }
  },
  
  watch: {
    '$route.query': {
      handler(newQuery) {
        const editParam = newQuery.edit;
        const clientIdParam = newQuery.clientId;
        
        console.log('Route query changed - editParam:', editParam, 'clientIdParam:', clientIdParam);
        console.log('Current state - isEditMode:', this.isEditMode, 'clientId:', this.clientId);
        
        if (editParam === 'true' && clientIdParam) {
          if (!this.isEditMode || this.clientId !== clientIdParam) {
            this.isEditMode = true;
            this.clientId = clientIdParam;
            console.log('Switching to edit mode for client:', clientIdParam);
            this.loadClientData();
          } else {
            console.log('Already in edit mode for this client');
          }
        } else if (this.isEditMode) {
          this.isEditMode = false;
          this.clientId = null;
          console.log('Switching to add mode');
          this.resetForm();
          this.generateCIF();
        } else {
          console.log('Staying in add mode');
        }
      },
      immediate: true
    }
  },
  
  async created() {
    console.log('AddClient component created');
  },

  async mounted() {
    console.log('AddClient component mounted');
    console.log('Current isEditMode:', this.isEditMode);
    console.log('Current newClient.cifNumber:', this.newClient.cifNumber);
    
    // Generate CIF number automatically when component is mounted (only for new clients)
    if (!this.isEditMode && !this.newClient.cifNumber) {
      console.log('Generating CIF number...');
      
      // First, try to generate a test CIF immediately for instant feedback
      const testCifNumber = CIFGenerator.generateTestCIF('individual');
      this.newClient.cifNumber = testCifNumber;
      console.log('Test CIF number set immediately:', testCifNumber);
      
      // Then try to generate a proper unique CIF
      try {
        const cifNumber = await CIFGenerator.generateCIF('individual');
        this.newClient.cifNumber = cifNumber;
        console.log('Unique CIF number generated and set:', cifNumber);
        console.log('newClient.cifNumber after setting:', this.newClient.cifNumber);
      } catch (error) {
        console.error('Error generating unique CIF, keeping test CIF:', error);
        console.log('Keeping test CIF number:', this.newClient.cifNumber);
      }
    } else {
      console.log('Skipping CIF generation - isEditMode:', this.isEditMode, 'cifNumber exists:', !!this.newClient.cifNumber);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 
